import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { ICurriculumResponse, ICurriculumResponseById, ICurriculumState } from '../../interface';
import {
  createCurriculum,
  fetchCurriculum,
  fetchCurriculums,
  updateCurriculum,
  updateOperationalTime,
} from '../action';

const initialState: ICurriculumState = {
  curriculums: [],
  curriculum: null,
  isLoading: false,
  isLoadingOperationalTime: false,
  error: null,
};

const curriculumSlice = createSlice({
  name: 'curriculum',
  initialState,
  reducers: {
    resetCurriculum: (state) => {
      state.curriculum = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurriculums.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.curriculums = [];
      })
      .addCase(fetchCurriculums.fulfilled, (state, action: PayloadAction<ICurriculumResponse>) => {
        state.isLoading = false;
        state.curriculums = action.payload.data.curriculum;
      })
      .addCase(fetchCurriculums.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_curriculums', 'Failed to fetch curriculums.');
        state.curriculums = [];
      })
      .addCase(fetchCurriculum.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.curriculum = null;
      })
      .addCase(
        fetchCurriculum.fulfilled,
        (state, action: PayloadAction<ICurriculumResponseById>) => {
          state.isLoading = false;
          state.curriculum = action.payload.data.curriculum;
          state.error = null;
        },
      )
      .addCase(fetchCurriculum.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_curriculums', 'Failed to fetch curriculums.');
        state.curriculum = null;
      })
      .addCase(createCurriculum.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCurriculum.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createCurriculum.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_create_curriculum', 'Failed to create curriculum.');
      })
      .addCase(updateCurriculum.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCurriculum.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateCurriculum.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_update_curriculum', 'Failed to update curriculum.');
      })
      .addCase(updateOperationalTime.pending, (state) => {
        state.isLoadingOperationalTime = true;
        state.error = null;
      })
      .addCase(updateOperationalTime.fulfilled, (state) => {
        state.isLoadingOperationalTime = false;
      })
      .addCase(updateOperationalTime.rejected, (state, action) => {
        state.isLoadingOperationalTime = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_update_operational_time', 'Failed to update operational time.');
      });
  },
});

export const { resetCurriculum } = curriculumSlice.actions;

export default curriculumSlice.reducer;
